import mime from "mime-types";

/**
 * Gets the file extension from a MIME type with special case handling.
 *
 * @param mimeType - The MIME type to convert to an extension
 * @returns The file extension (without the dot) or false if not found
 */
export function getExtensionFromMime(mimeType: string): false | string {
  // eslint-disable-next-line no-restricted-properties
  const extension = mime.extension(mimeType);

  // Special case handling
  if (extension === "qt" && mimeType === "video/quicktime") {
    return "mov";
  }

  return extension;
}

type AllowedExt =
  | "aep"
  | "ai"
  | "avi"
  | "bmp"
  | "cdr"
  | "csv"
  | "doc"
  | "docx"
  | "eps"
  | "fig"
  | "gif"
  | "heic"
  | "html"
  | "indd"
  | "jpeg"
  | "jpg"
  | "key"
  | "m4a"
  | "m4v"
  | "mid"
  | "midi"
  | "mov"
  | "mp3"
  | "mp4"
  | "mpeg"
  | "mpg"
  | "numbers"
  | "ogg"
  | "otf"
  | "pages"
  | "pdf"
  | "png"
  | "ppt"
  | "pptx"
  | "prproj"
  | "psb"
  | "psd"
  | "rar"
  | "raw"
  | "svg"
  | "tiff"
  | "ttf"
  | "txt"
  | "wav"
  | "webp"
  | "xd"
  | "xls"
  | "xlsx"
  | "zip";

const ALLOWED_EXTS = new Set<string>([
  "jpg",
  "png",
  "jpeg",
  "pdf",
  "heic",
  "svg",
  "eps",
  "webp",
  "tiff",
  "gif",
  "mp4",
  "m4v",
  "psb",
  "psd",
  "ai",
  "indd",
  "prproj",
  "aep",
  "fig",
  "mp3",
  "wav",
  "bmp",
  "xd",
  "html",
  "doc",
  "docx",
  "txt",
  "ppt",
  "key",
  "numbers",
  "pages",
  "cdr",
  "raw",
  "mid",
  "midi",
  "m4a",
  "ogg",
  "avi",
  "mpg",
  "mpeg",
  "rar",
  "zip",
  "xls",
  "xlsx",
  "csv",
  "mov",
  "ttf",
  "otf",
  "pptx",
]);

function splitFilename(originalFilename: string): {
  baseName: string;
  ext: string;
} {
  const name = originalFilename.trim();

  if (!name) throw new Error(`Invalid file name: ${name}`);

  // Use regex to validate and parse filename
  // Pattern: ^(?!\.)([^.]+(?:\.[^.]+)*)\.([^.]+)(?<!\.)$
  // - (?!\.) - negative lookahead: doesn't start with dot
  // - ([^.]+(?:\.[^.]+)*) - captures basename (can contain dots in middle)
  // - \. - literal dot separator
  // - ([^.]+) - captures extension (no dots)
  // - (?<!\.)$ - negative lookbehind: doesn't end with dot
  const filenamePattern = /^(?!\.)([^.]+(?:\.[^.]+)*)\.([^.]+)(?<!\.)$/;

  const match = name.match(filenamePattern);

  if (!match) {
    throw new Error(`Invalid file name or extension: ${name}`);
  }

  const [, baseName, ext] = match;

  // @ts-expect-error-next-line
  return { baseName, ext };
}

export function parseFileName(fileName: string): {
  baseName: string;
  ext: string;
  fullNormalizedName: string;
} {
  const { baseName, ext } = splitFilename(fileName);

  // Validate that the extension is allowed
  if (!ALLOWED_EXTS.has(ext.toLowerCase())) {
    throw new Error(`Invalid file extension: ${ext}`);
  }

  return {
    baseName,
    ext: ext.toLowerCase() as AllowedExt,
    fullNormalizedName: `${baseName}.${ext.toLowerCase()}`,
  };
}

export function getExtensionFromFilename(fileName: string): string {
  try {
    const { ext } = parseFileName(fileName);

    return ext;
  } catch {
    return "unknown";
    /*
     * Although this handling will no allow the file preview or normal download flow,
     * This will prevent the app from crashing in the case of an old file name.
     */
  }
}
